import { CSSProperties, ElementType, Fragment, ReactNode } from 'react';

import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Header, Table, flexRender } from '@tanstack/react-table';
import styled from 'styled-components';

import { IconChevronDownSmall } from '@components/icons/chevron-down-small';
import { IconMoveEnd } from '@components/icons/move-end';
import { IconMoveStart } from '@components/icons/move-start';
import { IconSortAsc } from '@components/icons/sort-asc';
import { IconSortDesc } from '@components/icons/sort-desc';
import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { Menu } from '@components/menu';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionSlowest, transitionStandard } from '@utils/transitions';

import { rowHeight } from './renderers';
import { Resizer } from './resizer';
import { useSortLocale } from './sorters';
import { TableStore } from './table-store';
import { stickyEndClassName } from './table-utils';
import { AvailableColumn, ColumnType, DataItemType } from './types';
import { getColumnWidth, getIsTableAction } from './use-column-sizing-vars';
import { DragBorder, HeaderContent } from './use-reorder-dnd';

type UseFixedHeaderContentParams<DataItem extends DataItemType> = {
  table: Table<DataItem>;
  store: TableStore;
  availableColumns: Array<AvailableColumn<DataItem>>;
  activeColumnTypeMap: Record<string, string>;
  fixedColumnIndex: number;
  fixedEndColumnsLength: number;
  additionalHeaderMenuItems?: (shortId: string) => ReactNode;
  translations: {
    hideField: string;
    moveToStart: string;
    moveToEnd: string;
  };
};

export const useFixedHeaderContent = <DataItem extends DataItemType>({
  table,
  store,
  availableColumns,
  activeColumnTypeMap,
  fixedColumnIndex,
  fixedEndColumnsLength,
  additionalHeaderMenuItems,
  translations,
}: UseFixedHeaderContentParams<DataItem>) => {
  const sortTranslations = useSortLocale();
  const { useColumnOrder, useColumnVisibility } = store;
  const { columnOrder, setColumnOrder } = useColumnOrder();
  const { columnVisibility, setColumnVisibility } = useColumnVisibility();
  const isOnlyVisibleColumn = Object.values(columnVisibility).filter(Boolean).length === 1;

  return () =>
    table.getHeaderGroups().map((headerGroup) => (
      <tr key={headerGroup.id}>
        <TableSpacer tag="th" />
        <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
          {headerGroup.headers.map((header, headerIndex) => {
            const headerSort = header.column.getIsSorted();
            const isSortable = header.column.getCanSort();
            const actual = availableColumns.find((c) => c.id === header.column.id);
            const sorterStrings =
              actual?.sortText ??
              sortTranslations[activeColumnTypeMap[header.id] as ColumnType] ??
              sortTranslations.text;
            const isFixedColumn = headerIndex === fixedColumnIndex;
            const isLastDataColumn =
              headerIndex === headerGroup.headers.length - 1 - fixedEndColumnsLength;

            return (
              <HeaderColumn
                key={header.column.id}
                store={store}
                header={header}
                isFixedColumn={isFixedColumn}
              >
                <Menu
                  placement="bottom"
                  buttonProps={{
                    style: menuStyleOverride,
                    variant: 'ghost-no-background',
                    children: (
                      <HeaderContent>
                        <Text color="textSecondary" font="semibold14">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Text>
                        <IconChevronDownSmall color="elementPurple50" data-slot="chevron" />
                      </HeaderContent>
                    ),
                  }}
                >
                  {({ close }) => (
                    <Fragment>
                      {actual?.menuHeader?.(close)}
                      {isSortable && (
                        <Fragment>
                          <Menu.Item
                            label={sorterStrings.asc}
                            icon={<IconSortAsc />}
                            isDisabled={headerSort === 'asc'}
                            onSelect={() => header.column.toggleSorting(false)}
                          />
                          <Menu.Item
                            label={sorterStrings.desc}
                            icon={<IconSortDesc />}
                            isDisabled={headerSort === 'desc'}
                            onSelect={() => header.column.toggleSorting(true)}
                          />
                          <Menu.Separator />
                        </Fragment>
                      )}
                      <Menu.Item
                        label={translations.moveToStart}
                        icon={<IconMoveStart />}
                        isDisabled={isFixedColumn}
                        onSelect={() => {
                          const newOrder = columnOrder.filter((id) => id !== header.id);
                          newOrder.unshift(header.id);
                          setColumnOrder(newOrder);
                        }}
                      />
                      <Menu.Item
                        label={translations.moveToEnd}
                        icon={<IconMoveEnd />}
                        isDisabled={isLastDataColumn}
                        onSelect={() => {
                          const newOrder = columnOrder.filter((id) => id !== header.id);
                          newOrder.push(header.id);
                          setColumnOrder(newOrder);
                        }}
                      />
                      <Menu.Separator />
                      <Menu.Item
                        label={translations.hideField}
                        isDisabled={isOnlyVisibleColumn}
                        icon={<IconVisibilityHidden />}
                        onSelect={() => {
                          setColumnVisibility({ ...columnVisibility, [header.id]: false });
                        }}
                      />
                      {additionalHeaderMenuItems?.(header.id)}
                      {actual?.menuFooter}
                    </Fragment>
                  )}
                </Menu>
              </HeaderColumn>
            );
          })}
        </SortableContext>
        <TableSpacer tag="th" />
        <TableSpacer tag="th" fill />
      </tr>
    ));
};

const HeaderColumn = <DataType extends DataItemType>({
  children,
  header,
  isFixedColumn,
  store,
}: {
  children: ReactNode;
  header: Header<DataType, unknown>;
  isFixedColumn: boolean;
  store: TableStore;
}) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: header.column.id,
    disabled: getIsTableAction(header.id),
    data: { header },
  });
  const width = getColumnWidth(header.id);
  const className = isFixedColumn ? stickyEndClassName : undefined;
  const isTableAction = getIsTableAction(header.id);

  return (
    <th
      className={className}
      id={`${header.id}-th`}
      key={header.id}
      style={{ transform: CSS.Translate.toString(transform), width }}
      ref={isTableAction ? undefined : setNodeRef}
    >
      {isTableAction ? (
        flexRender(header.column.columnDef.header, header.getContext())
      ) : (
        <HeaderContainer style={{ opacity: isDragging ? 0.5 : 1 }}>
          <DraggableHeaderContainer {...attributes} {...listeners}>
            {children}
          </DraggableHeaderContainer>
          {!isTableAction && <Resizer header={header} store={store} />}
          {isDragging && <DragBorder />}
        </HeaderContainer>
      )}
    </th>
  );
};

export const TableSpacer = ({
  tag: Tag = 'td',
  fill = false,
}: {
  tag?: ElementType;
  fill?: boolean;
}) => {
  return (
    <Tag className={fill ? 'table-space-fill' : 'table-spacer'}>
      <div />
    </Tag>
  );
};

const menuStyleOverride: CSSProperties = {
  width: '100%',
  height: '100%',
  justifyContent: 'flex-start',
  padding: 0,
  transform: 'none',
};

const DraggableHeaderContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '> div': {
    height: '100%',
  },
});

const HeaderContainer = styled.div({
  position: 'relative',
  display: 'flex',
  width: '100%',
  height: rowHeight,
  transition: transitionSlowest('background-color'),
  padding: `0 ${pxToRem(8)}`,

  [`${HeaderContent} [data-slot="chevron"]`]: {
    display: 'none',
    transition: transitionStandard('all'),
  },

  '[data-slot="drag-handle"]': {
    display: 'none',
  },

  '&:hover': {
    backgroundColor: theme.color.uiTrans10,
    [`${HeaderContent} [data-slot="chevron"]`]: {
      display: 'block',
    },

    '[data-slot="drag-handle"]': {
      display: 'flex',
    },
  },

  '> div:first-child': {
    width: '100%',
    height: '100%',
  },
});
